import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Tabs,
  Form,
  Input,
  Upload,
  DatePicker,
  Select,
  message,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleTwoTone,
  FileImageOutlined,
} from '@ant-design/icons';
import { useFirebase } from '../FirebaseContext';
import {
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import moment from 'moment';
import { toPickerDate } from '../utils/pickerDate';
import 'moment/locale/pl';
import './UserProfile.css';
import UmowyTab from './userProfile/UmowyTab';
import FakturyTab from './userProfile/FakturyTab';
import PoleceniaTab from './userProfile/PoleceniaTab';

moment.locale('pl');

const { Option } = Select;

/* ------------------ słowniki ----------------- */
const citizenshipOptions = [
  { label: 'Polska', value: 'Polska' },
  { label: 'Ukraina', value: 'Ukraina' },
  { label: 'Białoruś', value: 'Białoruś' },
  { label: 'Litwa', value: 'Litwa' },
  { label: 'Inny kraj', value: 'Inne' },
];

const settlementOptions = [
  { label: 'Tygodniowy', value: 'Tygodniowy' },
  { label: 'Dwutygodniowy', value: 'Dwutygodniowy' },
  { label: 'Miesięczny', value: 'Miesięczny' },
];

const statusOptions = [
  { label: 'Wybierz…', value: '' },
  { label: 'KYC', value: 'Wymagana weryfikacja KYC' },
  { label: 'Konto Aktywne', value: 'Konto Aktywne' },
  { label: 'Zablokuj', value: 'Zablokowany' },
];

/* --------------------------------------------- */
const UserProfile = ({ userId }) => {
  const { currentFirebase } = useFirebase();
  const firestore = currentFirebase.firestore;
  const storage = getStorage(currentFirebase.app);

  /* ---------------- state ---------------- */
  const [userData, setUserData] = useState({});
  const [docs, setDocs] = useState({});
  const [modalImg, setModalImg] = useState({ open: false, src: '', name: '' });
  const [edit, setEdit] = useState({ basic: false, dane_personalne: false, dane_podatkowe: false, dane_dodatkowe: false, samochod: false });

  /* ---------------- forms ---------------- */
  const [basicForm] = Form.useForm();
  const [personalForm] = Form.useForm();
  const [taxForm] = Form.useForm();
  const [extraForm] = Form.useForm();
  const [carForm] = Form.useForm();

  /* ------------- upload helper ----------- */
  const uploadFile = async (file, path) => {
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    return getDownloadURL(ref);
  };

  const uploadProps = (form, field, folder) => ({
    showUploadList: false,
    beforeUpload: () => false,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const ext = file.type.split('/')[1] || 'jpg';
        const url = await uploadFile(file, `users/${userId}/${folder}/${field}_${Date.now()}.${ext}`);
        form.setFieldValue(field, url);
        message.success('Plik zapisany');
        onSuccess('ok');
      } catch (e) {
        message.error('Błąd wysyłki');
        onError(e);
      }
    },
  });

  /* ------------ firestore utils ----------- */
  const getDocData = async (path) => {
    const snap = await getDoc(doc(firestore, ...path));
    return snap.exists() ? snap.data() : {};
  };

  const saveDoc = async (path, values, key) => {
    try {
      await setDoc(doc(firestore, ...path), values, { merge: true });
      message.success('Zapisano');
      if (key === 'basic') setUserData((p) => ({ ...p, ...values }));
      else setDocs((p) => ({ ...p, [key]: { ...p[key], ...values } }));
      setEdit((m) => ({ ...m, [key]: false }));
    } catch (e) {
      console.error('Firestore write error:', e);
      message.error('Błąd zapisu');
    }
  };

  /* ---------------- fetch all ------------- */
  useEffect(() => {
    const loadAll = async () => {
      const [u, dp, dpo, dd, sc] = await Promise.all([
        getDocData(['users', userId]),
        getDocData(['users', userId, 'dokumenty', 'dane_personalne']),
        getDocData(['users', userId, 'dokumenty', 'dane_podatkowe']),
        getDocData(['users', userId, 'dokumenty', 'dane_dodatkowe']),
        getDocData(['users', userId, 'dokumenty', 'samochod']),
      ]);
      setUserData(u);
      setDocs({ dane_personalne: dp, dane_podatkowe: dpo, dane_dodatkowe: dd, samochod: sc });
      /* reset forms */
      [basicForm, personalForm, taxForm, extraForm, carForm].forEach((f) => f.resetFields());
      basicForm.setFieldsValue(u);
      personalForm.setFieldsValue({ ...dp, dob: toPickerDate(dp.dob, 'YYYY-MM-DD') });
      taxForm.setFieldsValue(dpo);
      extraForm.setFieldsValue(dd);
      carForm.setFieldsValue(sc);
      setEdit({ basic: false, dane_personalne: false, dane_podatkowe: false, dane_dodatkowe: false, samochod: false });
    };
    if (userId) loadAll();
  }, [userId]);

  /* -------------- modal obrazka ----------- */
  const openImg = (src, name) => setModalImg({ open: true, src, name });
  const closeImg = () => setModalImg({ open: false, src: '', name: '' });
  const downloadImg = async () => {
    try {
      const blob = await (await fetch(modalImg.src)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${userData.firstName || 'dokument'}_${modalImg.name}.jpg`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch { message.error('Nie pobrano'); }
  };

  const header = (title, key) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{title}</span>
      <Button
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          setEdit((m) => ({ ...m, [key]: !m[key] }));
        }}
      >
        {edit[key] ? 'Anuluj' : 'Edytuj'}
      </Button>
    </div>
  );


  /* ------------------ PANELS ---------------- */
  const basicPanel = {
    key: 'basic',
    label: header('Informacje podstawowe', 'basic'),
    children: edit.basic ? (
      <Form form={basicForm} layout="vertical" onFinish={(v) => saveDoc(['users', userId], v, 'basic')}>
        <Form.Item label="Imię" name="firstName"><Input /></Form.Item>
        <Form.Item label="Nazwisko" name="lastName"><Input /></Form.Item>
        <Form.Item label="E‑mail" name="email"><Input disabled /></Form.Item>
        <Form.Item label="Telefon" name="phoneNumber"><Input /></Form.Item>
        <Form.Item label="Rola" name="role"><Input /></Form.Item>
        <Form.Item label="Status" name="status"><Select options={statusOptions} showSearch /></Form.Item>
        <Form.Item label="Obywatelstwo" name="citizenship"><Select options={citizenshipOptions} showSearch /></Form.Item>
        <Form.Item label="ID kierowcy" name="driverId"><Input /></Form.Item>
        <Form.Item label="Okres rozliczeniowy" name="okresrozliczeniowy"><Select options={settlementOptions} /></Form.Item>
        <Button type="primary" htmlType="submit">Zapisz</Button>
      </Form>
    ) : (
      <>
        <p><strong>Imię:</strong> {userData.firstName || 'brak'}</p>
        <p><strong>Nazwisko:</strong> {userData.lastName || 'brak'}</p>
        <p><strong>E‑mail:</strong> {userData.email || 'brak'}</p>
        <p><strong>Telefon:</strong> {userData.phoneNumber || 'brak'}</p>
        <p><strong>Rola:</strong> {userData.role || 'brak'}</p>
        <p><strong>Status:</strong> {userData.status || 'brak'}</p>
        <p><strong>Obywatelstwo:</strong> {userData.citizenship || 'brak'}</p>
        <p><strong>ID kierowcy:</strong> {userData.driverId || 'brak'}</p>
        <p><strong>Okres rozliczeń:</strong> {userData.okresrozliczeniowy || 'brak'}</p>
        <p><strong>Kraj:</strong> {userData.country || 'brak'}</p>
        <p><strong>Zarejestrowany:</strong> {userData.registeredAt?.toDate ? moment(userData.registeredAt.toDate()).format('DD-MM-YYYY HH:mm') : 'brak'}</p>
        <p><strong>Sumsub ID:</strong> {userData.sumsubApplicantId || 'brak'}</p>
      </>
    ),
  };

  /* ------- Dane osobowe ------- */
  const peselRule = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || value.length === 11) return Promise.resolve();
      return Promise.reject(new Error('PESEL powinien zawierać 11 cyfr'));
    },
  });

  const personalPanel = {
    key: 'dane_personalne',
    label: header('Dane osobowe', 'dane_personalne'),
    children: edit.dane_personalne ? (
      <Form
        form={personalForm}
        layout="vertical"
        onFinish={(v) => {
          if (v.street || v.postalCode || v.city) {
            const adr = `${v.street || ''} ${v.postalCode || ''} ${v.city || ''}`.trim();
            v.correspondenceAddress = adr; v.registrationAddress = adr; v.residenceAddress = adr;
          }
          if (v.dob && typeof v.dob?.format === 'function') {
            v.dob = v.dob.format('YYYY-MM-DD');   // Day.js lub Moment → string
          }

          saveDoc(['users', userId, 'dokumenty', 'dane_personalne'], v, 'dane_personalne');
        }}
      >
        <Form.Item label="Miasto" name="city"><Input /></Form.Item>
        <Form.Item label="Ulica" name="street"><Input /></Form.Item>
        <Form.Item label="Kod pocztowy" name="postalCode"><Input /></Form.Item>
        <Form.Item label="Data urodzenia" name="dob"><DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} /></Form.Item>
        <Form.Item label="PESEL" name="pesel" rules={[peselRule]}><Input /></Form.Item>
        <Button type="primary" htmlType="submit">Zapisz</Button>
      </Form>
    ) : docs.dane_personalne ? (
      <>
        <p><strong>Miasto:</strong> {docs.dane_personalne.city || 'brak'}</p>
        <p><strong>Ulica:</strong> {docs.dane_personalne.street || 'brak'}</p>
        <p><strong>Kod pocztowy:</strong> {docs.dane_personalne.postalCode || 'brak'}</p>
        <p><strong>Data ur.:</strong> {docs.dane_personalne.dob || 'brak'}</p>
        <p><strong>PESEL:</strong> {docs.dane_personalne.pesel || 'brak'}</p>
        <p><strong>Adres:</strong> {docs.dane_personalne.correspondenceAddress || 'brak'}</p>
      </>
    ) : 'Brak danych',
  };

  /* ------- Dane podatkowe ------- */
  const taxPanel = {
    key: 'dane_podatkowe',
    label: header('Dane podatkowe', 'dane_podatkowe'),
    children: edit.dane_podatkowe ? (
      <Form form={taxForm} layout="vertical" onFinish={(v) => saveDoc(['users', userId, 'dokumenty', 'dane_podatkowe'], v, 'dane_podatkowe')}>
        <Form.Item label="Rachunek bankowy" name="rachunekBankowy" rules={[{ pattern: /^\d{26}$/, message: 'Numer konta musi zawierać dokładnie 26 cyfr' }]}><Input maxLength={26} /></Form.Item>
        <Form.Item label="Urząd skarbowy" name="urzadSkarbowy"><Input /></Form.Item>
        <Form.Item label="Okres rozliczeniowy" name="okresrozliczeniowy"><Select options={settlementOptions} /></Form.Item>
        <Button type="primary" htmlType="submit">Zapisz</Button>
      </Form>
    ) : docs.dane_podatkowe ? (
      <>
        <p><strong>Rachunek:</strong> {docs.dane_podatkowe.rachunekBankowy || 'brak'}</p>
        <p><strong>Urząd skarbowy:</strong> {docs.dane_podatkowe.urzadSkarbowy || 'brak'}</p>
        <p><strong>Okres rozliczeń:</strong> {docs.dane_podatkowe.okresrozliczeniowy || 'brak'}</p>
      </>
    ) : 'Brak danych',
  };

  /* ------- Dane dodatkowe ------- */
  const extraFiles = [
    { label: 'Badania lekarskie', field: 'badaniaLekarskie' },
    { label: 'Badania psychologiczne', field: 'badaniaPsychologiczne' },
    { label: 'Dowód osobisty przód', field: 'dowodOsobistyPrzod' },
    { label: 'Dowód osobisty tył', field: 'dowodOsobistyTyl' },
    { label: 'Zaświadczenie o niekaralności', field: 'niekaralnosc' },
    { label: 'Zdjęcie profilowe', field: 'zdjecieProfilowe' },
  ];

  const extraPanel = {
    key: 'dane_dodatkowe',
    label: header('Dane dodatkowe', 'dane_dodatkowe'),
    children: edit.dane_dodatkowe ? (
      <Form form={extraForm} layout="vertical" onFinish={(v) => saveDoc(['users', userId, 'dokumenty', 'dane_dodatkowe'], v, 'dane_dodatkowe')}>
        {extraFiles.map(({ label, field }) => (
          <Form.Item key={field} label={label} name={field} valuePropName="file">
            <Upload {...uploadProps(extraForm, field, 'dane_dodatkowe')}>
              <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
            </Upload>
            {extraForm.getFieldValue(field) && <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginLeft: 8 }} />}
          </Form.Item>
        ))}
        <Button type="primary" htmlType="submit">Zapisz</Button>
      </Form>
    ) : docs.dane_dodatkowe ? (
      <>
        {extraFiles.map(({ label, field }) => (
          <p key={field}>
            <strong>{label}:</strong>{' '}
            {docs.dane_dodatkowe[field] ? (
              <Button type="link" icon={<FileImageOutlined />} onClick={() => openImg(docs.dane_dodatkowe[field], field)}>Zobacz</Button>
            ) : 'brak'}
          </p>
        ))}
      </>
    ) : 'Brak danych',
  };

  /* ------- Samochód ------- */
  const carFiles = [
    { label: 'Dowód rejestracyjny przód', field: 'dowodPrzod' },
    { label: 'Dowód rejestracyjny tył', field: 'dowodTyl' },
    { label: 'Ubezpieczenie', field: 'ubezpieczenie' },
  ];

  const carPanel = {
    key: 'samochod',
    label: header('Dane samochodu', 'samochod'),
    children: edit.samochod ? (
      <Form form={carForm} layout="vertical" onFinish={(v) => saveDoc(['users', userId, 'dokumenty', 'samochod'], v, 'samochod')}>
        {['carOwnership', 'carMake', 'carModel', 'carRegistrationNumber', 'carVin', 'carYear'].map((f) => (
          <Form.Item key={f} label={f} name={f}><Input /></Form.Item>
        ))}
        {['dowodPrzod', 'dowodTyl', 'ubezpieczenie'].map((f) => (
          <Form.Item key={f} label={f} name={f} valuePropName="file">
            <Upload {...uploadProps(carForm, f, 'samochod')}>
              <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
            </Upload>
          </Form.Item>
        ))}
        <Button type="primary" htmlType="submit">Zapisz</Button>
      </Form>
    ) : docs.samochod ? (
      <>
        <p><strong>Własność:</strong> {docs.samochod.carOwnership || 'Brak'}</p>
        {docs.samochod.carOwnership === 'Własny' && (
          <>
            <p><strong>Marka:</strong> {docs.samochod.carMake || 'Brak'}</p>
            <p><strong>Model:</strong> {docs.samochod.carModel || 'Brak'}</p>
            <p><strong>Rejestracja:</strong> {docs.samochod.carRegistrationNumber || 'Brak'}</p>
            <p><strong>VIN:</strong> {docs.samochod.carVin || 'Brak'}</p>
            <p><strong>Rok:</strong> {docs.samochod.carYear || 'Brak'}</p>
            {['dowodPrzod', 'dowodTyl', 'ubezpieczenie'].map((f) => (
              <p key={f}>
                <strong>{f}:</strong>{' '}
                {docs.samochod[f] ? (
                  <Button type="link" onClick={() => openImg(docs.samochod[f], f)}>Zobacz</Button>
                ) : 'Brak'}
              </p>
            ))}
          </>
        )}
      </>
    ) : 'Brak danych',
  };

  const tabItems = [
    { key: 'basic', label: 'Podstawowe', children: <>{basicPanel.label}{basicPanel.children}</> },
    { key: 'dane_personalne', label: 'Osobowe', children: <>{personalPanel.label}{personalPanel.children}</> },
    { key: 'dane_podatkowe', label: 'Podatkowe', children: <>{taxPanel.label}{taxPanel.children}</> },
    { key: 'dane_dodatkowe', label: 'Dokumenty', children: <>{extraPanel.label}{extraPanel.children}</> },
    { key: 'samochod', label: 'Samochód', children: <>{carPanel.label}{carPanel.children}</> },
    {
      key: 'umowy',
      label: 'Umowy',
      children: <UmowyTab userId={userId} />,
    },
    {
      key: 'faktury',
      label: 'Faktury',
      children: <FakturyTab userData={userData} />,
    },
    {
      key: 'polecenia',
      label: 'Polecenia',
      children: <PoleceniaTab userId={userId} userData={userData} />,
    },
  ];

  return (
    <div className="user-profile">
      <Tabs items={tabItems} size="small" />

      <Modal open={modalImg.open} onCancel={closeImg} footer={null} centered width={600}>
        <img src={modalImg.src} alt={modalImg.name} style={{ width: '100%' }} />
        <Button style={{ marginTop: 16, borderRadius: 8 }} onClick={downloadImg}>Pobierz</Button>
      </Modal>
    </div>
  );
};

export default UserProfile;
